import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react'
import { useGlobalEvent } from '@highlight/ipc/react'
import ShellIpc from '@renderer/ipc/ShellIpc.renderer'
import type { ShellInfo, ShellSessionId, OpenShellArgs } from '@shared/types/ShellTypes'
import { LOCAL_TOOLS_FLAG_KEY } from '@shared/config/local-tools'
import { LaunchDarklyRenderer } from '@renderer/utils/launchdarkly.renderer'

const noop = () => {}

interface ShellWithTitle extends ShellInfo {
  title: string
}

interface HighlightShellsContextValue {
  shells: Array<ShellWithTitle>
  isLoading: boolean
  refresh: () => Promise<void>
  spawnShell: (args?: OpenShellArgs) => Promise<ShellInfo>
  killShell: (id: ShellSessionId) => Promise<boolean>
  openShell: (id: ShellSessionId) => Promise<void>
}

const HighlightShellsContext = createContext<HighlightShellsContextValue | null>(null)

export function HighlightShellsProvider({ children }: { children: ReactNode }) {
  const isEnabled = LaunchDarklyRenderer.get<boolean>(LOCAL_TOOLS_FLAG_KEY, false)
  const [shells, setShells] = useState<Array<ShellWithTitle>>([])
  const [isLoading, setIsLoading] = useState(isEnabled)
  const titlesRef = useRef(new Map<ShellSessionId, string>())

  const refresh = useCallback(async () => {
    if (!isEnabled) return
    try {
      const list = await ShellIpc.listShells()
      setShells(
        list.map((info) => ({
          ...info,
          title: titlesRef.current.get(info.id) ?? '',
        })),
      )
    } catch (error) {
      console.error('Failed to list shells:', error)
    }
  }, [isEnabled])

  useEffect(() => {
    if (!isEnabled) return
    const load = async () => {
      try {
        await refresh()
      } catch (error) {
        console.error('Failed to load initial shells:', error)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [refresh, isEnabled])

  const handleShellSpawned = useCallback((info: ShellInfo) => {
    setShells((prev) => {
      if (prev.some((s) => s.id === info.id)) return prev
      return [...prev, { ...info, title: '' }]
    })
  }, [])

  const handleShellExited = useCallback(({ id }: { id: ShellSessionId }) => {
    titlesRef.current.delete(id)
    setShells((prev) => prev.filter((s) => s.id !== id))
  }, [])

  const handleShellTitle = useCallback(({ id, title }: { id: ShellSessionId; title: string }) => {
    titlesRef.current.set(id, title)
    setShells((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)))
  }, [])

  useGlobalEvent('shell:spawned', isEnabled ? handleShellSpawned : noop)
  useGlobalEvent('shell:exited', isEnabled ? handleShellExited : noop)
  useGlobalEvent('shell:title', isEnabled ? handleShellTitle : noop)

  const spawnShell = useCallback(async (args: OpenShellArgs = {}) => {
    try {
      return await ShellIpc.spawnShell(args)
    } catch (error) {
      console.error('Failed to spawn shell:', error)
      throw error
    }
  }, [])

  const killShell = useCallback(async (id: ShellSessionId) => {
    try {
      return await ShellIpc.killShell(id)
    } catch (error) {
      console.error('Failed to kill shell:', error)
      throw error
    }
  }, [])

  const openShell = useCallback(async (id: ShellSessionId) => {
    try {
      await ShellIpc.openShell(id)
    } catch (error) {
      console.error('Failed to open shell:', error)
    }
  }, [])

  const value = useMemo(
    () => ({ shells, isLoading, refresh, spawnShell, killShell, openShell }),
    [shells, isLoading, refresh, spawnShell, killShell, openShell],
  )

  return <HighlightShellsContext.Provider value={value}>{children}</HighlightShellsContext.Provider>
}

export function useHighlightShells(): HighlightShellsContextValue {
  const ctx = useContext(HighlightShellsContext)
  if (!ctx) {
    throw new Error('useHighlightShells must be used within a HighlightShellsProvider')
  }
  return ctx
}

export function useIsShellAlive(shellSessionId: string | undefined): boolean {
  const { shells } = useHighlightShells()
  if (!shellSessionId) return false
  return shells.some((s) => s.id === shellSessionId)
}
